import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";
import { Sparkles, Trash2, Save, Crown } from "lucide-react";

export default function BrandVoice() {
  const navigate = useNavigate();
  const { status } = useSubscription();
  const [samples, setSamples] = useState(["", "", ""]);
  const [voice, setVoice] = useState("");
  const [loading, setLoading] = useState(false);
  const [training, setTraining] = useState(false);

  useEffect(() => {
    api.get("/brand-voice/me").then((r) => setVoice(r.data.voice || "")).finally(() => setLoading(false));
  }, []);

  const isPro = !!status?.is_pro;

  const setSample = (i) => (e) => {
    const next = [...samples];
    next[i] = e.target.value;
    setSamples(next);
  };

  const train = async () => {
    const clean = samples.map((s) => s.trim()).filter((s) => s.length > 20);
    if (clean.length < 1) return toast.error("Add at least one post (20+ chars).");
    setTraining(true);
    try {
      const res = await api.post("/brand-voice/train", { samples: clean });
      setVoice(res.data.voice);
      toast.success("Brand voice locked in.");
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Brand Voice is a Pro feature.", {
          action: { label: "Upgrade", onClick: () => navigate("/app/pricing") },
        });
      } else toast.error("Training failed.");
    } finally { setTraining(false); }
  };

  const del = async () => {
    if (!confirm("Delete your brand voice? Future generations will use the default style.")) return;
    await api.delete("/brand-voice/me");
    setVoice("");
    toast.success("Brand voice cleared.");
  };

  if (loading) return <div className="p-10 font-mono text-xs text-zinc-500">// loading</div>;

  return (
    <div className="p-6 md:p-10 lg:p-12 space-y-8" data-testid="brand-voice-page">
      <header className="border-b border-white/10 pb-6">
        <div className="label-eyebrow flex items-center gap-2">
          <Sparkles size={12} className="text-signal-yellow" /> // module 06 · pro
        </div>
        <h1 className="heading-display text-4xl sm:text-5xl mt-2">BRAND <span className="text-signal-yellow">VOICE</span>.</h1>
        <p className="font-mono text-xs text-zinc-500 mt-3 max-w-2xl">
          // paste 3 posts you're proud of. Claude analyzes your style. every future hook + remix matches your voice.
        </p>
      </header>

      {!isPro && (
        <div className="brutal-border border-signal-yellow bg-signal-yellow/5 p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Crown size={20} className="text-signal-yellow" />
            <div className="font-mono text-xs text-zinc-300">Brand Voice is a Pro feature. Upgrade to unlock.</div>
          </div>
          <button onClick={() => navigate("/app/pricing")} data-testid="brand-upgrade" className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-2 hover:bg-signal-yellow-hover transition-colors">
            Upgrade →
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="label-eyebrow">// paste your past posts</div>
          {samples.map((s, i) => (
            <textarea
              key={i}
              data-testid={`voice-sample-${i}`}
              value={s}
              onChange={setSample(i)}
              placeholder={`Sample ${i + 1} — paste a post you love (min 20 chars)`}
              className="w-full bg-[#141414] border border-white/10 text-white placeholder:text-zinc-600 focus:border-signal-yellow focus:outline-none p-3 font-mono text-xs min-h-[100px]"
            />
          ))}
          <button
            data-testid="voice-train"
            onClick={train}
            disabled={training || !isPro}
            className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-6 py-3 hover:bg-signal-yellow-hover transition-colors disabled:opacity-40 inline-flex items-center gap-2"
          >
            <Save size={12} /> {training ? "Training..." : "Train my voice →"}
          </button>
        </div>
        <div className="brutal-border bg-[#141414]">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <span className="label-eyebrow">// your voice · locked in</span>
            {voice && (
              <button data-testid="voice-delete" onClick={del} className="text-signal-red hover:underline font-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
                <Trash2 size={10} /> Clear
              </button>
            )}
          </div>
          <div className="p-5 min-h-[300px]">
            {voice ? (
              <pre data-testid="voice-preview" className="font-mono text-xs text-zinc-100 whitespace-pre-wrap break-words leading-relaxed">{voice}</pre>
            ) : (
              <div className="font-mono text-xs text-zinc-500">// no voice set. train one to see your distilled style rules here.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
